/**
 * Riga di coda a COLONNE, per la coda a tutta larghezza (Centro Ticket admin,
 * Smistamento, area associativa). Veste «A · Tabella pulita» (2026-10-27):
 *
 *   • | Ticket | Richiedente | Canale | Stato | Priorita | Aperto | Assegnati | Attivita
 *
 * - Messaggio del richiedente non letto: tutta la riga in verde tenue con una
 *   barra verde a sinistra (e «Nuovo messaggio» nel nome accessibile).
 * - •: pallino arancio della nota interna da leggere.
 * - Ticket: oggetto in peso normale fino a 2 righe (`--s3-subj-lines`) poi
 *   ellissi, testo intero nel `title`; sotto il numero, in grassetto mono.
 * - Richiedente: stesso clamp a 2 righe, nome intero nel `title`.
 *   Sotto, un piccolo tag di tipo: Ospite, Studio/Autoscuola/Nautica (categoria
 *   dell'associato), Utente interno; senza categoria nessun tag.
 * - Canale: quadratino o avatar del canale e nome (fino a 2 righe, segno in
 *   alto accanto alla prima); nome intero e origine nel `title`.
 * - Stato: glifo e testo; sotto, solo se c'e da guardare, la nota SLA
 *   (in ritardo / in scadenza / in pausa). Il chip SLA di prima (col tempo) e
 *   la scadenza stanno nel `title` della cella: nessuna colonna SLA.
 * - Priorita: simbolo (colonna stretta «Prio»), legenda sopra la tabella.
 * - Aperto: data di apertura (gg/mm/aaaa). Assegnati: avatar 24 px sovrapposti.
 * - Tag: NON in riga, nel `title` della riga.
 *
 * Stessa struttura in v3 e v4: cambiano solo i pezzi della veste (glifo e
 * swatch v4, avatar con anello) e i colori, presi dai token di ciascuna.
 * `QueueTableHeader` e l'intestazione, con la stessa griglia; con `onSort` le
 * colonne ordinabili sono bottoni (ordinamento lato server). Sotto i 1024px
 * `SupportQueue` non usa questa riga (torna alle card, invariate).
 */
import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { S3Avatar } from './primitives';
import { slaQueueNote, slaQueueTitle, statusGlyph, type S3SlaBadge } from './visual';
import { ariaSortFor, nextColumnSort, type ColumnSort, type ColumnSortKey, type QueueRowModel } from './queueModel';
import { useSupportLook } from '../v4/lookContext';
import ChannelSwatch from '../v4/ChannelSwatch';
import ChannelAvatarMark from '../ChannelAvatarMark';
import { hasChannelAvatar } from '../channelAvatar';
import { channelTone } from '../v4/channelPalette';
import StatusGlyph from '../v4/StatusGlyph';
import { DraftsBadge, V4Avatars, V4Rating } from '../v4/QueueRowParts';
import PriorityIcon from './PriorityIcon';
import DerivedStatusMark from '../DerivedStatusMark';

export interface QueueTableRowProps {
  row: QueueRowModel;
  sla: S3SlaBadge | null;
  onOpen: (id: string) => void;
}

/** Colonne dell'intestazione nell'ordine della griglia; `sort` = chiave ordinabile. */
const HEADER_COLUMNS: { label: string; sort?: ColumnSortKey; right?: boolean; center?: boolean; short?: string }[] = [
  { label: 'Ticket', sort: 'number' },
  { label: 'Richiedente', sort: 'requester' },
  { label: 'Canale', sort: 'channel' },
  { label: 'Stato', sort: 'status' },
  // Colonna stretta: scritto «Prio», nome accessibile e title «Priorità».
  { label: 'Priorità', sort: 'priority', center: true, short: 'Prio' },
  { label: 'Aperto', sort: 'created' },
  { label: 'Assegnati' },
  { label: 'Attività', sort: 'activity', right: true },
];

export interface QueueTableHeaderProps {
  /** Ordinamento di colonna attivo (freccia, `aria-pressed` e descrizione). */
  sort?: ColumnSort | null;
  /** Clic su una colonna ordinabile; senza, l'intestazione e solo testo (come prima). */
  onSort?: (key: ColumnSortKey) => void;
}

/** Descrizione del bottone: stato attuale e cosa fa il prossimo clic. */
const sortTitle = (label: string, key: ColumnSortKey, sort: ColumnSort | null): string => {
  const name = label.toLowerCase();
  if (!sort || sort.key !== key) return `Ordina per ${name}`;
  const now = sort.dir === 'asc' ? 'crescente' : 'decrescente';
  const next = nextColumnSort(sort, key);
  const then = next ? (next.dir === 'asc' ? 'ordine crescente' : 'ordine decrescente') : 'torna all’ordine predefinito';
  return `Ordinato per ${name} (${now}): clic per ${then}`;
};

const headClass = (v4: boolean) => `s3-trow s3-trow--head${v4 ? ' s4-trow' : ''}`;

/**
 * Intestazione. Senza `onSort` e decorativa (`aria-hidden`, come prima). Con
 * `onSort` e un GRUPPO di bottoni d'ordinamento: le righe sono bottoni, non celle
 * di una tabella, quindi niente `role="table"`/`columnheader`/`aria-sort`. La
 * colonna attiva ha `aria-pressed="true"`, colore d'accento e freccia; direzione
 * e prossimo clic stanno nel `title`, il nome resta l'etichetta. «Assegnati» e la
 * colonna del pallino non sono ordinabili.
 */
export const QueueTableHeader: React.FC<QueueTableHeaderProps> = ({ sort = null, onSort }) => {
  const v4 = useSupportLook() === 'v4';
  if (!onSort) {
    return (
      <div className={headClass(v4)} role="row" aria-hidden="true">
        <span />
        {HEADER_COLUMNS.map((c) => (
          <span
            key={c.label}
            style={c.right ? { textAlign: 'right' } : c.center ? { textAlign: 'center' } : undefined}
            title={c.short ? c.label : undefined}
          >
            {c.short || c.label}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className={headClass(v4)} role="group" aria-label="Ordina l’elenco dei ticket">
      <span aria-hidden="true" />
      {HEADER_COLUMNS.map((c) => {
        if (!c.sort) {
          return (
            <span key={c.label} aria-hidden="true">
              {c.label}
            </span>
          );
        }
        const key = c.sort;
        const aria = ariaSortFor(sort, key);
        const Icon = aria === 'ascending' ? ArrowUp : aria === 'descending' ? ArrowDown : ArrowUpDown;
        return (
          <span
            key={c.label}
            style={c.right ? { display: 'flex', justifyContent: 'flex-end' } : c.center ? { display: 'flex', justifyContent: 'center' } : undefined}
          >
            <button
              type="button"
              className={`s3-thsort${aria !== 'none' ? ' s3-thsort--on' : ''}`}
              aria-pressed={aria !== 'none'}
              aria-label={c.short ? c.label : undefined}
              title={sortTitle(c.label, key, sort)}
              onClick={() => onSort(key)}
            >
              {c.short || c.label}
              <Icon size={11} aria-hidden="true" className={aria === 'none' ? 's3-thsort__idle' : undefined} />
            </button>
          </span>
        );
      })}
    </div>
  );
};

/** Quadratino del canale in v3 (la v4 ha `ChannelSwatch` con i suoi token). */
const ChannelMarkV3: React.FC<{ channel: QueueRowModel['channel'] }> = ({ channel }) =>
  hasChannelAvatar(channel) ? (
    <ChannelAvatarMark channel={channel} size={14} />
  ) : (
    <span className="s3-tch__sq" style={{ background: channelTone(channel).color }} aria-hidden="true" />
  );

/** Avatar 24 px sovrapposti in v3 (in v4 `V4Avatars`, con foto e anello). */
const AvatarsV3: React.FC<{ row: QueueRowModel }> = ({ row }) => (
  <span className="s3-tavs">
    {row.assigneeInitials.map((ini, i) => (
      <S3Avatar key={row.assigneeKeys[i]} initials={ini} size={24} title={row.assigneeNames[i]} src={row.assigneePhotos[i]} />
    ))}
    {row.extraAssignees > 0 && <span className="s3-tavs__more">+{row.extraAssignees}</span>}
  </span>
);

const QueueTableRow: React.FC<QueueTableRowProps> = ({ row, sla, onOpen }) => {
  const v4 = useSupportLook() === 'v4';
  const note = slaQueueNote(sla);
  const glyph = statusGlyph(row.statusKey);
  const noAssignees = row.assigneeInitials.length === 0 && row.extraAssignees === 0;
  const cls = [
    's3-trow',
    v4 ? 's4-trow' : '',
    row.selected ? 's3-trow--on' : '',
    row.unread ? 's3-trow--unread' : '',
    v4 && !row.active ? 's4-row--off' : '',
  ].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      className={cls}
      aria-current={row.selected ? 'true' : undefined}
      onClick={() => onOpen(row.id)}
      title={row.tagsTitle}
    >
      {/* Messaggio del richiedente non letto: riga verde (classe) e «Nuovo messaggio»
          nel nome accessibile. Nota interna da leggere: pallino arancio. */}
      <span className="s3-tdots">
        {row.unread && <span className="s3-sr">Nuovo messaggio</span>}
        {row.unreadNote && (
          <>
            <span className={`s3-dot-note${v4 ? ' s4-rhomb' : ''}`} aria-hidden="true" title="Nota da leggere" />
            <span className="s3-sr">Nota da leggere</span>
          </>
        )}
      </span>

      {/* Ticket: l'oggetto intero (e i tag) nel tooltip della cella */}
      <span className="s3-tcell" title={row.tagsTitle ? `${row.subject} · ${row.tagsTitle}` : row.subject}>
        <span className="s3-tsubj s3-tclamp">{row.subject}</span>
        <span className="s3-tnum">
          #{row.number}
          <DraftsBadge count={row.pendingDrafts} />
        </span>
      </span>

      {/* Richiedente */}
      <span className="s3-tcell" title={row.requesterLabel || undefined}>
        <span className="s3-treq s3-tclamp">{row.requesterLabel}</span>
        {row.requesterTag && (
          <span className={`s3-ttag s3-ttag--${row.requesterTag.kind}`}>{row.requesterTag.label}</span>
        )}
      </span>

      {/* Canale: nome fino a 2 righe, segno allineato alla prima; nome intero e
          origine (Email, Area riservata, …) nel tooltip */}
      <span
        className="s3-tcell s3-tcell--line s3-tcell--top"
        title={[row.groupName, row.origin.label ? `Origine: ${row.origin.label}` : ''].filter(Boolean).join(' · ') || undefined}
      >
        {row.groupName && (
          <>
            <span className="s3-tch__mark">
              {v4 ? <ChannelSwatch channel={row.channel} size={10} /> : <ChannelMarkV3 channel={row.channel} />}
            </span>
            <span className="s3-tchname s3-tclamp">{row.groupName}</span>
          </>
        )}
      </span>

      {/* Stato + nota SLA; il chip SLA di prima e la scadenza nel tooltip */}
      <span className="s3-tcell" title={row.statusDisplay.title || slaQueueTitle(sla)}>
        {row.statusDisplay.derived ? (
          <DerivedStatusMark d={row.statusDisplay} />
        ) : v4 ? (
          <StatusGlyph status={row.statusKey} withLabel />
        ) : (
          <span className="s3-tstate">
            <span className={`s3-sg s3-sg--${glyph.kind}`} aria-hidden="true" />
            {glyph.label}
          </span>
        )}
        {note && (
          <span className={`s3-tsla s3-tsla--${note.kind}`} aria-hidden="true">
            {note.text}
          </span>
        )}
        {/* Etichetta completa di prima (col tempo), per chi non usa il mouse */}
        {sla && <span className="s3-sr">{sla.label}</span>}
      </span>

      {/* Priorita: simbolo */}
      <span className="s3-tcell s3-tcell--center">
        <PriorityIcon priority={row.priorityKey} />
      </span>

      {/* Aperto */}
      <span className="s3-tcell">
        <span className="s3-tmut">{row.openedAt}</span>
      </span>

      {/* Assegnati */}
      <span className="s3-tcell s3-tcell--av">
        {noAssignees ? (
          <span className="s3-tmut" title="Nessun assegnatario">
            —
          </span>
        ) : v4 ? (
          <V4Avatars row={row} size={24} />
        ) : (
          <AvatarsV3 row={row} />
        )}
      </span>

      {/* Attivita */}
      <span className="s3-tcell s3-tcell--end">
        <span className="s3-tmut">{row.age}</span>
        {row.rating !== null && <V4Rating stars={row.rating} />}
      </span>
    </button>
  );
};

export default QueueTableRow;
